import {
  AppBar,
  Badge,
  Box,
  IconButton,
  Stack,
  Toolbar,
  Typography
} from '@mui/material'
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined'
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined'
import weppaLogoText from '@/assets/images/weppa-logo-text.svg'
import ModuleMenuContainer from '@/core/layouts/header/moduleMenu/ModuleMenuContainer.tsx'
import MainMenuItem from '@/core/layouts/header/MainMenuItem.tsx'
import ToolsMenuContainer from '@/core/layouts/header/toolsMenu/ToolsMenuContainer.tsx'
import UserMenu from '@/core/layouts/header/userMenu/UserMenu.tsx'
import { useLocation, useNavigate } from 'react-router-dom'
import CalculatorModal from '../../../modules/utilities/calculator/components/calculator/CalculatorModal.tsx'
import { useAppDispatch, useAppSelector } from '@/globalStore/hooks.ts'
import { globalActions } from '@/globalStore/globalSlice/globalSlice.ts'
import RenderWithPermissions from '@/core/components/renderWithPermissions/RenderWithPermissions.tsx'
import { appConfig } from '@/config/app.ts'
import useHasPermissions from '@/core/hooks/useHasPermissions.ts'
import { useMemo } from 'react'
import { getViewUrlFromModule } from '@/core/utils/general.ts'
import PopperItem from '@/core/layouts/header/PopperItem.tsx'
import { useGetAccessibleViewFromModule } from '@/core/hooks/useGetAccessibleViewFromModule.ts'
import NotificationPaper from '@/modules/utilities/notification/components/notificationPopover/NotificationPaper.tsx'
import useNotification from '@/core/hooks/useNotification.ts'
import UninstallAppWrapper from '@/modules/utilities/uninstallApp/components/containers/UninstallAppWrapper.tsx'

interface Props {
  showSideBtn?: boolean
  showWeppaLogo?: boolean
}

const Header = ({ showSideBtn = true, showWeppaLogo = true }: Props) => {
  const { isSidebarOpen, currentModuleName, loggedUser } = useAppSelector(
    (state) => state.global
  )
  const isCalculatorModalOpen = useAppSelector(
    (state) => state.calculator.modalOpen
  )
  const dispatch = useAppDispatch()
  const { getFirstAccessibleView } = useGetAccessibleViewFromModule()
  const { hasPermissions } = useHasPermissions()
  const navigate = useNavigate()
  const { notifications, isFetching, handlerMarkAllAsRead, isPending } =
    useNotification()
  const { pathname } = useLocation()
  const isHomepage = pathname === '/'

  const handleShowSidebar = () =>
    dispatch(globalActions.setIsSidebarOpen(!isSidebarOpen))

  const allowedModules = useMemo(
    () =>
      appConfig.systemModules
        .filter((module) => {
          if (module.systemName === 'marketplace' && loggedUser)
            return (
              (loggedUser.isWeppa || loggedUser.hasMarketplace) &&
              module.views.some((view) => hasPermissions([view.permission]))
            )
          return module.views.some((view) => hasPermissions([view.permission]))
        })
        .map((module, i) => {
          const firstAccessibleView = getFirstAccessibleView(module)
          if (!firstAccessibleView) return undefined

          const moduleName = module.systemName

          if (currentModuleName === moduleName) return undefined

          const viewName = firstAccessibleView.systemName

          const accessibleUrl = getViewUrlFromModule(
            moduleName,
            viewName as string
          )

          return (
            <PopperItem
              key={i}
              onClick={() => {
                dispatch(globalActions.setCurrentModuleName(module.systemName))
                navigate(accessibleUrl)
              }}
              sx={{ width: '100px', height: '80px' }}
            >
              <Box
                component="img"
                src={module.icon}
                sx={{
                  width: '40px',
                  height: '40px',
                  aspectRatio: '1/1',
                  objectFit: 'contain'
                }}
              ></Box>
              <Typography variant="body2" color="text.secondary">
                {module.name}
              </Typography>
            </PopperItem>
          )
        })
        .filter((m) => m !== undefined),
    [
      currentModuleName,
      dispatch,
      getFirstAccessibleView,
      hasPermissions,
      navigate
    ]
  )

  return (
    <header>
      <AppBar
        position="static"
        elevation={0}
        color="transparent"
        sx={{ backgroundColor: 'white' }}
      >
        <Toolbar
          disableGutters
          sx={{ justifyContent: 'space-between', px: '25px' }}
        >
          <Stack direction="row" gap={3}>
            {showSideBtn && (
              <IconButton
                sx={{ marginLeft: '-8px' }}
                onClick={handleShowSidebar}
              >
                <NotesOutlinedIcon />
              </IconButton>
            )}
            {showWeppaLogo && <Box component="img" src={weppaLogoText}></Box>}
          </Stack>
          <Stack
            direction="row"
            sx={{ flexGrow: 0, alignItems: 'center', gap: 1 }}
          >
            <RenderWithPermissions
              requiredPermissions={[
                'SALES_TOOLBOX:DESINSTALAR',
                'SALES_TOOLBOX:CALCULADORA'
              ]}
            >
              <MainMenuItem
                menuItem={<HandymanOutlinedIcon />}
                menuPopper={<ToolsMenuContainer />}
              />
            </RenderWithPermissions>
            {allowedModules.length > 0 && !isHomepage && (
              <MainMenuItem
                menuItem={<GridViewOutlinedIcon />}
                menuPopper={<ModuleMenuContainer modules={allowedModules} />}
              />
            )}
            {!hasPermissions(['SALES_CLIENTS:COBRANZA']) && (
              <MainMenuItem
                menuItem={
                  <Badge badgeContent={notifications.unread} color="error">
                    <NotificationsNoneOutlinedIcon />
                  </Badge>
                }
                menuPopper={
                  <NotificationPaper
                    notifications={notifications.last}
                    isFetching={isFetching}
                    isMarking={isPending}
                    handleMarkAll={handlerMarkAllAsRead}
                  />
                }
              />
            )}
            <UserMenu />
          </Stack>
        </Toolbar>
        {/*All Modals or DEEPS*/}
        {isCalculatorModalOpen && <CalculatorModal />}
        <UninstallAppWrapper />
      </AppBar>
    </header>
  )
}

export default Header
